import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Plane, LayoutDashboard, Radio, ShieldCheck, Clock } from "lucide-react";
import { REGISTER_URL, LOGIN_URL, isAuthed, isAdmin, getMeta } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { MigrationBanner } from "@/components/landing/MigrationBanner";
import { useMigrationBanner } from "@/hooks/useMigrationBanner";

const LINKS = [
  { label: "About", href: "/#about" },
  { label: "Live Stats", href: "/#stats" },
  { label: "Leaderboard", href: "/#leaderboard" },
  { label: "Pilots", href: "/roster", internal: true },
  { label: "News", href: "/news", internal: true },
  { label: "Events", href: "/events", internal: true },
  { label: "Activity", href: "/activity", internal: true },
  { label: "Tours", href: "/tours", internal: true },
];

const ThemeSliderControl = ({ themePosition, onPositionChange, onPositionCommit }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        data-testid="theme-slider-trigger"
        aria-label="Adjust time-of-day lighting"
        className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--aiv-accent)]/30 text-[var(--aiv-accent)] transition-colors duration-200 hover:bg-[var(--aiv-accent)]/10"
      >
        <Clock className="h-4 w-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent
      data-testid="theme-slider-popover"
      align="start"
      className="w-64 border-white/10 bg-[#12141D] text-[#FDFBF7]"
    >
      <div className="mb-3 flex items-center justify-between text-overline text-[#A1A1AA]">
        <span>Dusk</span>
        <span>Dawn</span>
        <span>Noon</span>
      </div>
      <Slider
        data-testid="theme-slider"
        min={0}
        max={100}
        step={0.1}
        value={[themePosition]}
        onValueChange={([v]) => onPositionChange(v)}
        onValueCommit={([v]) => onPositionCommit(v)}
      />
    </PopoverContent>
  </Popover>
);

export const Navbar = ({ themePosition, onPositionChange, onPositionCommit }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { visible: bannerVisible, dismiss: dismissBanner } = useMigrationBanner();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <MigrationBanner visible={bannerVisible} dismiss={dismissBanner} />
    <header
      data-testid="main-navbar"
      className={`fixed left-0 right-0 z-50 transition-colors duration-300 ${
        bannerVisible ? "top-9" : "top-0"
      } ${scrolled ? "glass" : "bg-transparent"}`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-10">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#8A1538] shadow-[0_0_20px_rgba(212,175,55,0.2)]">
            <Plane className="h-5 w-5 -rotate-45 text-[var(--aiv-accent)]" />
          </span>
          <span className="leading-tight">
            <span className="block font-display text-lg font-bold tracking-tight text-[var(--aiv-text)]">
              Air India Virtual
            </span>
            <span className="text-overline block text-[0.6rem] text-[var(--aiv-accent)]">
              Since 2009
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-5 xl:flex">
          <Link
            to="/live-flights"
            data-testid="nav-link-live-flights"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-white transition-colors duration-200 hover:text-[#39FF14]"
          >
            <Radio className="h-3.5 w-3.5 text-[#8A1538]" /> Live Flights
          </Link>
          <Link
            to="/ranks"
            data-testid="nav-link-ranks"
            className="text-sm font-medium text-white transition-colors duration-200 hover:text-[#39FF14]"
          >
            Ranks
          </Link>
          {LINKS.map((l) =>
            l.internal ? (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-white transition-colors duration-200 hover:text-[#39FF14]"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-white transition-colors duration-200 hover:text-[#39FF14]"
              >
                {l.label}
              </a>
            )
          )}
          <ThemeSliderControl
            themePosition={themePosition}
            onPositionChange={onPositionChange}
            onPositionCommit={onPositionCommit}
          />
        </div>

        <div className="hidden items-center gap-3 xl:flex">
          {isAuthed() ? (
            <>
              {isAdmin() && (
                <a
                  href={getMeta()?.admin_dashboard_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-admin-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--aiv-accent)]/40 px-5 py-2.5 text-sm font-semibold text-[var(--aiv-accent)] transition-colors duration-200 hover:bg-[var(--aiv-accent)]/10"
                >
                  <ShieldCheck className="h-4 w-4" /> Admin
                </a>
              )}
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#8A1538] px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#A81A45]"
              >
                <LayoutDashboard className="h-4 w-4" /> {isAdmin() ? "Profile" : "Dashboard"}
              </Link>
            </>
          ) : (
            <>
              <Link
                to={LOGIN_URL}
                data-testid="nav-login-btn"
                className="text-sm font-semibold text-[var(--aiv-text)] transition-colors hover:text-[var(--aiv-accent)]"
              >
                Login
              </Link>
              <Link
                to={REGISTER_URL}
                data-testid="nav-register-btn"
                className="rounded-full bg-[#8A1538] px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#A81A45]"
              >
                Become a Pilot
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="text-[var(--aiv-text)] xl:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div className="glass border-t border-white/10 xl:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-4 px-6 py-6">
            <Link
              to="/live-flights"
              onClick={() => setOpen(false)}
              data-testid="mobile-live-flights-btn"
              className="inline-flex items-center gap-2 text-base font-medium text-[var(--aiv-text-2)] hover:text-[var(--aiv-text)]"
            >
              <Radio className="h-4 w-4 text-[#8A1538]" /> Live Flights
            </Link>
            <Link
              to="/ranks"
              onClick={() => setOpen(false)}
              data-testid="mobile-link-ranks"
              className="text-base font-medium text-[var(--aiv-text-2)] hover:text-[var(--aiv-text)]"
            >
              Ranks
            </Link>
            {LINKS.map((l) =>
              l.internal ? (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-[var(--aiv-text-2)] hover:text-[var(--aiv-text)]"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-[var(--aiv-text-2)] hover:text-[var(--aiv-text)]"
                >
                  {l.label}
                </a>
              )
            )}
            <div className="mt-2 flex items-center gap-3">
              <span className="text-base font-medium text-[var(--aiv-text-2)]">Lighting</span>
              <ThemeSliderControl
                themePosition={themePosition}
                onPositionChange={onPositionChange}
                onPositionCommit={onPositionCommit}
              />
            </div>
            {isAuthed() ? (
              <>
                {isAdmin() && (
                  <a
                    href={getMeta()?.admin_dashboard_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-admin-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-[var(--aiv-accent)]/40 px-6 py-3 text-center text-sm font-semibold text-[var(--aiv-accent)]"
                  >
                    <ShieldCheck className="h-4 w-4" /> Admin Dashboard
                  </a>
                )}
                <Link
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-dashboard-btn"
                  className="mt-1 rounded-full bg-[#8A1538] px-6 py-3 text-center text-sm font-semibold text-white"
                >
                  {isAdmin() ? "Pilot Profile" : "Dashboard"}
                </Link>
              </>
            ) : (
              <>
                <Link
                  to={LOGIN_URL}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-login-btn"
                  className="text-base font-semibold text-[var(--aiv-text)]"
                >
                  Login
                </Link>
                <Link
                  to={REGISTER_URL}
                  onClick={() => setOpen(false)}
                  className="mt-2 rounded-full bg-[#8A1538] px-6 py-3 text-center text-sm font-semibold text-white"
                >
                  Become a Pilot
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
    </>
  );
};
